import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import type { HippoAnimation, HippoPersonalityLevel } from './hippo'

interface HippoContextValue {
  personalityLevel: HippoPersonalityLevel
  setPersonalityLevel: (level: HippoPersonalityLevel) => void
  activePanelId: string | null
  showHippoOnPanel: (panelId: string, animation?: HippoAnimation) => void
  hideHippo: () => void
  shouldShowHippo: (panelId: string) => boolean
  getCurrentAnimation: (panelId: string) => HippoAnimation
  triggerAnimation: (panelId: string, animation: HippoAnimation) => void
}

const HippoContext = createContext<HippoContextValue | null>(null)

interface HippoProviderProps {
  children: ReactNode
  initialPersonalityLevel?: HippoPersonalityLevel
}

/**
 * HippoProvider manages the global hippo personality system.
 * 
 * Implements the Single Hippo Rule: Only one hippo appears at a time
 * on the currently active/focused panel.
 * 
 * Personality Levels:
 * - 'none': No hippos displayed
 * - 'default': Single hippo on active panel only  
 * - 'everywhere': Easter egg mode - hippos on all panels
 */
export function HippoProvider({ 
  children, 
  initialPersonalityLevel = 'default' 
}: HippoProviderProps) {
  const [personalityLevel, setPersonalityLevel] = useState<HippoPersonalityLevel>(initialPersonalityLevel)
  const [activePanelId, setActivePanelId] = useState<string | null>(null)
  const [panelAnimations, setPanelAnimations] = useState<Record<string, HippoAnimation>>({})

  // Single Hippo Rule: Only one hippo can be active at a time
  const showHippoOnPanel = (panelId: string, animation: HippoAnimation = 'idle') => {
    setActivePanelId(panelId)
    setPanelAnimations(prev => ({
      ...prev,
      [panelId]: animation
    }))
  }

  const hideHippo = () => {
    setActivePanelId(null)
    setPanelAnimations({})
  }

  const shouldShowHippo = (panelId: string) => {
    switch (personalityLevel) {
      case 'none':
        return false
      case 'everywhere':
        return true // Easter egg mode - hippos everywhere!
      case 'default':
      default:
        return activePanelId === panelId
    }
  }

  const getCurrentAnimation = (panelId: string): HippoAnimation => {
    return panelAnimations[panelId] || 'idle'
  }

  const triggerAnimation = (panelId: string, animation: HippoAnimation) => {
    // Only trigger if this panel should show a hippo
    if (shouldShowHippo(panelId)) {
      setPanelAnimations(prev => ({
        ...prev,
        [panelId]: animation
      }))
      
      // Return to idle after animation completes
      if (animation !== 'idle') {
        setTimeout(() => {
          setPanelAnimations(prev => ({
            ...prev,
            [panelId]: 'idle'
          }))
        }, getAnimationDuration(animation))
      }
    }
  }

  const value: HippoContextValue = {
    personalityLevel,
    setPersonalityLevel,
    activePanelId,
    showHippoOnPanel,
    hideHippo,
    shouldShowHippo,
    getCurrentAnimation,
    triggerAnimation,
  }

  return (
    <HippoContext.Provider value={value}>
      {children}
    </HippoContext.Provider>
  )
}

/**
 * Hook to access hippo personality system
 */
export function useHippoPersonality() {
  const context = useContext(HippoContext)
  if (!context) {
    throw new Error('useHippoPersonality must be used within a HippoProvider')
  }
  return context
}

/**
 * Hook for panels to manage their hippo presence
 */
export function usePanelHippo(panelId: string) {
  const {
    shouldShowHippo,
    getCurrentAnimation,
    triggerAnimation,
    showHippoOnPanel,
    hideHippo
  } = useHippoPersonality()

  const isVisible = shouldShowHippo(panelId)
  const animation = getCurrentAnimation(panelId)

  const show = useCallback((initialAnimation?: HippoAnimation) => {
    showHippoOnPanel(panelId, initialAnimation)
  }, [panelId, showHippoOnPanel])

  const hide = useCallback(() => {
    hideHippo()
  }, [hideHippo])

  const animate = useCallback((animation: HippoAnimation) => {
    triggerAnimation(panelId, animation)
  }, [panelId, triggerAnimation])

  // Convenience methods for common animations
  const celebrate = () => animate('earFlap')
  const suggest = () => animate('earWiggle')  
  const alert = () => animate('nostrilBlow')

  return {
    isVisible,
    animation,
    show,
    hide,
    animate,
    celebrate,
    suggest,
    alert,
  }
}

// Helper function to get animation durations for auto-return to idle
function getAnimationDuration(animation: HippoAnimation): number {
  switch (animation) {
    case 'earWiggle':
      return 900 // 9 frames × 100ms = 900ms total
    case 'nostrilBlow':
      return 900 // 0.3s * 3 repetitions
    case 'earFlap':
      return 600
    default:
      return 1000
  }
}